import {
	faCheckCircle,
	faSpinner,
	faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type {
	AccessProofInputModel,
	AccessRequestVisitorOutcome,
	FormDefinitionModel,
	FormFieldDefinitionModel,
	PublicFormDefinitionDto,
} from '@supply-mover-2-0/shared';
import { REGISTRATION_FORM_KEY } from '@supply-mover-2-0/shared';
import {
	darkTheme,
	FormAccessProofQuestion,
	FormRenderer,
	useFormRenderer,
	useTheme,
	useTranslation,
	type FormAccessCodeSenderModel,
	type FormFileUploaderModel,
} from '@supply-mover-2-0/web-ui';
import type { CSSProperties, JSX } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTurnstile } from '../hooks/useTurnstile';
import {
	fetchFormDefinition,
	sendAccessCode,
	submitForm,
	uploadFormFile,
	type FormUploadOutcomeModel,
} from '../services/publicFormsService';

/**
 * `'modal'` is the dark glass panel a dark-login-theme brand gets (white text on a dark,
 * translucent card); `'page'` is the brand surface that follows the storefront's light or
 * dark mode.
 */
type Variant = 'modal' | 'page';

interface RegistrationFormProps {
	variant?: Variant;
	/** Called from the thank-you screen's Done button. */
	onComplete?: () => void;
}

type LoadStateModel =
	| { state: 'loading' }
	| { state: 'ready'; form: PublicFormDefinitionDto }
	| { state: 'unavailable' }
	| { state: 'error' };

/**
 * PD-16574 — the storefront's registration form, drawn from the brand's registration
 * form in Workd Forms (docs/claude/forms/02-registration-migration.md). The questions,
 * which are required and their wording come from the form definition; the shared
 * renderer checks answers as the visitor goes, exactly as the server will; Turnstile and
 * the honeypot guard the submit.
 *
 * PD-16713: a file question uploads each file as it is picked (uploadFormFile). The first
 * upload of this form fill carries the page's Turnstile token and opens the fill's upload
 * session on the server; the rest ride on it. The form cannot be sent while a file is still
 * on its way.
 *
 * PD-17590: a form with access requests asks "Do you have an account with us?" first. For
 * Yes the page draws the proof block itself (FormAccessProofQuestion) and keeps the proofs
 * out of the answers: they go in the submit body's own `accessProof` key. Send code asks
 * Workd to send a code to the customer's own email or phone on file, with a fresh spam-check
 * token each time. The server's answer says whether the login is on its way or the request
 * went to the team; a proof that did not check out comes back as a message to try again.
 */
export function RegistrationForm({
	variant = 'page',
	onComplete,
}: RegistrationFormProps): JSX.Element {
	const { t } = useTranslation();
	const [load, setLoad] = useState<LoadStateModel>({ state: 'loading' });
	const isModal = variant === 'modal';

	useEffect(() => {
		let active = true;
		fetchFormDefinition(REGISTRATION_FORM_KEY)
			.then((form) => {
				if (active) {
					setLoad(
						form != null ? { state: 'ready', form } : { state: 'unavailable' },
					);
				}
			})
			.catch(() => {
				if (active) {
					setLoad({ state: 'error' });
				}
			});
		return (): void => {
			active = false;
		};
	}, []);

	const messageCls = isModal
		? 'text-center text-sm text-white/80 py-8'
		: 'text-center text-sm text-brand-muted py-8';

	if (load.state === 'loading') {
		return (
			<p className={messageCls}>
				<FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
				{t('Loading…')}
			</p>
		);
	}
	if (load.state === 'unavailable') {
		return (
			<p className={messageCls}>
				{t(
					'Registration is not currently available. Please contact your sales representative.',
				)}
			</p>
		);
	}
	if (load.state === 'error') {
		return (
			<p className={messageCls}>
				{t(
					'We could not load the registration form. Please refresh the page to try again.',
				)}
			</p>
		);
	}
	return (
		<RegistrationFormBody
			form={load.form}
			variant={variant}
			onComplete={onComplete}
		/>
	);
}

/** utm_* values from the page address, kept with the submission for reporting. */
function attributionFromUrl(): Record<string, string> | undefined {
	const attribution: Record<string, string> = {};
	for (const [key, value] of new URLSearchParams(window.location.search)) {
		if (key.startsWith('utm_') && value.length > 0) {
			attribution[key] = value.slice(0, 200);
		}
	}
	return Object.keys(attribution).length > 0 ? attribution : undefined;
}

// The dark glass panel sits on the brand's dark login backdrop whatever mode the
// storefront is in, so the renderer inside it is given the dark theme's token values.
// The tokens are CSS variables written to the page root, so setting them on this
// wrapper re-points every token class below it and nothing outside it.
//
// The dark theme's grays are made for the CRM's gray surfaces and read as muddy gray on a
// colored brand backdrop, so supporting text and outlines use translucent white instead,
// the way the rest of the Register page does. `--fg-muted` is what `text-muted` resolves to
// (the shared Tailwind preset, which the storefront no longer overrides since PD-16744), and
// the renderer uses it for field hints, the signature hint and section subtitles; the border
// tokens draw the section rules, the signature box outline and the Clear button.
const DARK_PANEL_TOKENS = {
	...darkTheme.cssVars,
	'--fg-muted': 'rgba(255, 255, 255, 0.7)',
	'--border-default': 'rgba(255, 255, 255, 0.25)',
	'--border-strong': 'rgba(255, 255, 255, 0.4)',
} as CSSProperties;

function RegistrationFormBody({
	form,
	variant,
	onComplete,
}: {
	form: PublicFormDefinitionDto;
	variant: Variant;
	onComplete?: () => void;
}): JSX.Element {
	const { t } = useTranslation();
	const { isDark } = useTheme();
	const isModal = variant === 'modal';

	const definition: FormDefinitionModel = useMemo(
		() => ({
			fields: [...form.fields]
				.sort((a, b) => a.sortOrder - b.sortOrder)
				// The public definition leaves out what only staff see.
				.map(
					({ accessProof, ...field }): FormFieldDefinitionModel => ({
						...field,
						adminLabel: null,
						identity: null,
						includeInNotification: false,
						// PD-17590 — the proof block reads its settings where the CRM keeps them.
						fieldOptions:
							accessProof != null ? { accessProof: { ...accessProof } } : null,
					}),
				),
			rules: form.rules,
			// PD-16687 — sections, side-by-side rows and text, as arranged on the Layout tab.
			layout: form.layout,
		}),
		[form],
	);
	// One id per filled-in form, resent on a retry so a double submit is stored once. It also
	// ties this fill's uploads to its submission.
	const submissionId = useRef(crypto.randomUUID());
	const startedAt = useRef(new Date().toISOString());
	const [honeypot, setHoneypot] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [banner, setBanner] = useState<string | null>(null);
	const [thanks, setThanks] = useState<string | null>(null);
	// PD-17590 — what the thanks screen says: a registration, or an access request's outcome.
	const [thanksKind, setThanksKind] = useState<
		'registration' | AccessRequestVisitorOutcome
	>('registration');
	// PD-17590 — the access request's proofs, never part of the answers.
	const [accessProof, setAccessProof] = useState<AccessProofInputModel>({});

	const turnstile = useTurnstile({
		siteKey: form.turnstileSiteKey,
		action: form.formKey,
		theme: isModal || isDark ? 'dark' : 'light',
	});

	// PD-16713 — whether this fill's upload session is open on the server, so later uploads
	// need no token. Set once the server got past its spam check for an upload of ours.
	const uploadSessionOpen = useRef(false);
	const { getToken, reset: resetTurnstile } = turnstile;
	const uploadFile = useCallback<FormFileUploaderModel>(
		async ({ fieldKey, uploadId, file }) => {
			const send = async (
				withToken: boolean,
			): Promise<FormUploadOutcomeModel | null> => {
				let turnstileToken: string | undefined;
				if (withToken && form.turnstileSiteKey != null) {
					turnstileToken = (await getToken()) ?? undefined;
					if (turnstileToken == null) {
						return null;
					}
				}
				try {
					return await uploadFormFile(
						form.formKey,
						{
							submissionId: submissionId.current,
							uploadId,
							fieldKey,
							turnstileToken,
						},
						file,
					);
				} finally {
					// Tokens are single-use: the submit gets a fresh one.
					if (turnstileToken != null) {
						resetTurnstile();
					}
				}
			};

			let outcome = await send(!uploadSessionOpen.current);
			if (outcome?.body.formErrors?.includes('upload_session_required') === true) {
				// The session lapsed (hours idle): open a new one.
				uploadSessionOpen.current = false;
				outcome = await send(true);
			}
			if (outcome == null) {
				return {
					ok: false,
					code: null,
					message: t(
						'Please complete the verification check, then attach the file again.',
					),
				};
			}
			const refusal = outcome.body.errors?.[fieldKey] ?? null;
			if (outcome.body.success || refusal != null) {
				uploadSessionOpen.current = true;
			}
			if (outcome.body.success && outcome.body.upload != null) {
				return {
					ok: true,
					name: outcome.body.upload.name,
					sizeBytes: outcome.body.upload.sizeBytes,
				};
			}
			return {
				ok: false,
				code: refusal,
				message: refusal == null ? outcome.body.message : null,
			};
		},
		[form.formKey, form.turnstileSiteKey, getToken, resetTurnstile, t],
	);
	const state = useFormRenderer(definition, undefined, { uploadFile });
	const hasFileQuestion = form.fields.some(
		(field) => field.fieldType === 'file',
	);
	// PD-17590 — the proof block, and whether this fill is an access request (it shows).
	const proofField =
		form.fields.find((field) => field.fieldType === 'accessProof') ?? null;
	// PD-17590 — the proof block's Send code needs a spam-check token as early as a file
	// upload does, so a form with either places the check at the top (see below). A form
	// with Create logins with proof off has no proofs to give, so no Send code either.
	const spamCheckAtTop =
		hasFileQuestion ||
		(proofField?.accessProof != null && proofField.accessProof.proofs.length > 0);
	const isAccessRequest =
		proofField != null &&
		!state.validation.hiddenKeys.includes(proofField.fieldKey);
	const customerNumberValue =
		proofField?.accessProof != null
			? state.values[proofField.accessProof.customerNumberFieldKey]
			: undefined;
	const customerNumber =
		typeof customerNumberValue === 'string' ? customerNumberValue.trim() : '';

	const sendCode = useCallback<FormAccessCodeSenderModel>(
		async (channel) => {
			let turnstileToken: string | undefined;
			if (form.turnstileSiteKey != null) {
				turnstileToken = (await getToken()) ?? undefined;
				if (turnstileToken == null) {
					return {
						ok: false,
						message: 'Please complete the verification check, then send the code again.',
					};
				}
			}
			try {
				const outcome = await sendAccessCode(form.formKey, {
					customerNumber,
					channel,
					turnstileToken,
					locale: form.locale,
				});
				return { ok: outcome.body.success, message: outcome.body.message };
			} finally {
				// Tokens are single-use: the next send and the submit each get a fresh one.
				if (turnstileToken != null) {
					resetTurnstile();
				}
			}
		},
		[
			customerNumber,
			form.formKey,
			form.locale,
			form.turnstileSiteKey,
			getToken,
			resetTurnstile,
		],
	);

	const handleSubmit = async (event: React.FormEvent): Promise<void> => {
		event.preventDefault();
		if (isSubmitting) {
			return;
		}
		setBanner(null);
		// PD-16713 — a file still on its way is not part of the answer yet.
		if (state.pendingUploads > 0) {
			setBanner(t('Wait for your files to finish uploading, then submit.'));
			return;
		}
		const result = state.attempt();
		if (!result.valid) {
			return;
		}
		setIsSubmitting(true);
		// Whether this attempt spent a token. One that never got a token leaves the widget
		// alone: resetting it would restart a check the visitor may be completing.
		let spentToken = false;
		try {
			let turnstileToken: string | undefined;
			if (form.turnstileSiteKey != null) {
				turnstileToken = (await turnstile.getToken()) ?? undefined;
				if (turnstileToken == null) {
					setBanner(t('Please complete the verification check.'));
					return;
				}
			}
			spentToken = turnstileToken != null;
			const outcome = await submitForm(form.formKey, {
				submissionId: submissionId.current,
				answers: result.answers,
				turnstileToken,
				startedAt: startedAt.current,
				attribution: attributionFromUrl(),
				locale: form.locale,
				website: honeypot,
				// PD-17590 — only an access request sends proofs.
				...(isAccessRequest ? { accessProof } : {}),
			});
			if (outcome.body.success) {
				setThanks(outcome.body.message);
				setThanksKind(outcome.body.accessRequest ?? 'registration');
			} else if (
				outcome.body.formErrors?.some(
					(code) => code === 'access_not_verified' || code === 'access_locked',
				) === true
			) {
				// PD-17590 — the proofs did not check out (or too many tries): said next to the
				// button, the same words whether or not the customer number exists.
				setBanner(t(outcome.body.message));
			} else {
				state.setServerErrors(outcome.body);
			}
		} catch {
			setBanner(
				t(
					'We could not send your registration. Please check your connection and try again.',
				),
			);
		} finally {
			// Tokens are single-use: every attempt that sent one needs a fresh check.
			if (spentToken) {
				turnstile.reset();
			}
			setIsSubmitting(false);
		}
	};

	if (thanks != null) {
		return (
			<div
				className={
					isModal
						? 'text-center py-6'
						: 'text-center py-12 px-4 max-w-md mx-auto'
				}
			>
				<div
					className={
						isModal
							? 'mx-auto w-12 h-12 rounded-full bg-portal-accent/20 flex items-center justify-center mb-4'
							: 'mx-auto w-16 h-16 rounded-full bg-portal-accent/15 flex items-center justify-center mb-5'
					}
				>
					<FontAwesomeIcon
						icon={faCheckCircle}
						className={
							isModal
								? 'text-2xl text-portal-accent'
								: 'text-3xl text-portal-accent'
						}
					/>
				</div>
				<h3
					className={
						isModal
							? 'text-base font-semibold text-white mb-2'
							: 'text-2xl font-semibold text-default mb-3'
					}
				>
					{thanksKind === 'login_on_its_way'
						? t('Your login is on its way')
						: thanksKind === 'received'
							? t('Request sent')
							: t(thanks)}
				</h3>
				<p
					className={
						isModal
							? 'text-sm text-white/75 mb-6'
							: 'text-base text-brand-muted mb-6'
					}
				>
					{thanksKind === 'registration'
						? t(
								'Our team will review your registration and reach out shortly to confirm your account and walk through next steps.',
							)
						: t(thanks)}
				</p>
				{onComplete != null && (
					<button
						type="button"
						onClick={onComplete}
						className={
							isModal
								? 'inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full bg-portal-accent hover:bg-portal-accent-hover text-white text-sm font-semibold shadow-lg shadow-black/30 transition-colors'
								: 'inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-portal-accent hover:bg-portal-accent-hover text-white text-sm font-semibold shadow-lg transition-colors'
						}
					>
						{t('Done')}
					</button>
				)}
			</div>
		);
	}

	return (
		<form
			onSubmit={(event) => {
				void handleSubmit(event);
			}}
			className="space-y-5"
			noValidate
			style={isModal ? DARK_PANEL_TOKENS : undefined}
		>
			{/* PD-16713 — with a file question the spam check must be ready by the first
			    upload, which can come long before the visitor scrolls to the button; it only
			    renders once its place is on screen, so its place is the top of the form. It
			    draws only when it needs the visitor to interact. PD-17590 — the same holds for
			    the proof block's Send code. */}
			{spamCheckAtTop && <div ref={turnstile.containerRef} />}

			<FormRenderer
				definition={definition}
				state={state}
				idPrefix="register"
				disabled={isSubmitting}
				renderCustomField={(field) =>
					field.fieldType === 'accessProof' ? (
						<FormAccessProofQuestion
							field={field}
							value={accessProof}
							onChange={(next) => {
								setAccessProof(next);
								setBanner(null);
							}}
							onSendCode={sendCode}
							customerNumber={customerNumber}
							idPrefix="register"
							disabled={isSubmitting}
						/>
					) : null
				}
			/>

			{/* Honeypot: off screen and out of the tab order, so people never fill it and
			    bots usually do. The server stores such a submission as spam. */}
			<div
				aria-hidden="true"
				style={{
					position: 'absolute',
					left: '-9999px',
					height: 0,
					width: 0,
					overflow: 'hidden',
				}}
			>
				<label htmlFor="register-website-hp">{t('Website')}</label>
				<input
					id="register-website-hp"
					type="text"
					tabIndex={-1}
					autoComplete="off"
					value={honeypot}
					onChange={(event) => {
						setHoneypot(event.target.value);
					}}
				/>
			</div>

			{/* Turnstile draws here only when it needs the visitor to interact. */}
			{!spamCheckAtTop && <div ref={turnstile.containerRef} />}

			{(banner != null || turnstile.failed) && (
				<div
					role="alert"
					className="flex items-start gap-2 text-sm rounded-lg px-3 py-2.5 bg-danger-badge text-danger-tint-fg border border-danger-tint"
				>
					<FontAwesomeIcon
						icon={faTriangleExclamation}
						className="mt-0.5 shrink-0"
					/>
					<span>
						{banner ??
							t(
								'The verification check could not load. Please refresh the page and try again.',
							)}
					</span>
				</div>
			)}

			<button
				type="submit"
				disabled={isSubmitting}
				className={
					isModal
						? 'w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-portal-accent hover:bg-portal-accent-hover disabled:opacity-60 text-white text-sm font-semibold transition-colors min-h-touch'
						: 'w-full flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-portal-accent hover:bg-portal-accent-hover disabled:opacity-60 text-white text-sm font-semibold shadow-lg transition-colors min-h-touch'
				}
			>
				{(isSubmitting || state.pendingUploads > 0) && (
					<FontAwesomeIcon icon={faSpinner} spin />
				)}
				{isSubmitting
					? t('Submitting…')
					: state.pendingUploads > 0
						? t('Uploading files…')
						: isAccessRequest
							? t('Request Access')
							: t('Submit Registration')}
			</button>

			<p
				className={
					isModal
						? 'text-[11px] text-white/55 leading-relaxed'
						: 'text-xs text-brand-muted leading-relaxed'
				}
			>
				{t(
					'By submitting, you confirm that the contact details above are accurate. Our team will review your information and follow up to set up your account.',
				)}
			</p>
		</form>
	);
}
